import { FC, useEffect, useRef } from 'react';
import s from './dashboard-charts.module.css';
import cn from 'classnames';
import { ChartProps, useDashboardCharts } from './use-dashboard-charts';
import { Card } from '../../../../../../components/card/card';
import Chart from 'react-apexcharts';
import { Tooltip } from '../../../../../../features/tooltip/tooltip';
import { DashboardChartsWidget } from './dashboard-charts-widget/dashboard-charts-widget';
import { DashboardEmpty } from '../dashboard-empty/dashboard-empty';

// @ts-ignore
Apex.chart = {
  locales: [
    {
      name: 'en',
      options: {
        toolbar: {
          selectionZoom: 'Selection Zoom',
          pan: 'Panning',
          reset: 'Reset Zoom',
        },
      },
    },
    {
      name: 'ru',
      options: {
        toolbar: {
          selectionZoom: 'Выбрать область',
          pan: 'Перемещение',
          reset: 'Сбросить масштаб',
        },
      },
    },
    {
      name: 'kz',
      options: {
        toolbar: {
          selectionZoom: 'Таңдау',
          pan: 'Панорама',
          reset: 'Масштабты қалпына келтіру',
        },
      },
    },
    {
      name: 'ch',
      options: {
        toolbar: {
          selectionZoom: '选择区域',
          pan: '平移',
          reset: '重置缩放',
        },
      },
    },
  ],
};

interface Props {
  className?: string;
  chartsData: ChartProps[];
  /** Внешний мониторинг: своя сетка `chartsGrid` по :nth-child; без дублирования `.feature > :first-child`. */
  externalChartsGrid?: boolean;
}

export const DashboardCharts: FC<Props> = ({
  className,
  chartsData,
  externalChartsGrid,
}) => {
  const { t } = useDashboardCharts();
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!externalChartsGrid) return;
    const el = gridRef.current;
    if (!el) return;
    let tid: ReturnType<typeof setTimeout>;
    const notifyResize = () => {
      window.clearTimeout(tid);
      tid = window.setTimeout(() => {
        requestAnimationFrame(() => {
          window.dispatchEvent(new Event('resize'));
        });
      }, 120);
    };
    const ro = new ResizeObserver(notifyResize);
    ro.observe(el);
    return () => {
      ro.disconnect();
      window.clearTimeout(tid);
    };
  }, [externalChartsGrid, chartsData.length]);

  return (
    <div
      ref={gridRef}
      className={cn(s.feature, externalChartsGrid && s.layoutExternalCharts, className)}
    >
      {chartsData.map(
        (
          {
            label,
            hidden,
            subLabel,
            labelTooltip,
            tooltipPanelClassName,
            labelInfoIcon,
            wrapped,
            invalidData,
            filters,
            footer,
            fullWidth,
            ...other
          },
          index,
        ) => (
          <Card key={index} className={cn(fullWidth && s.chartItemFullWidth, hidden && s.chartItemHidden)}>
            <div className={s.label}>
              <div className={s.labelTitleBlock}>
                <span>{t(label)}</span>
                {labelTooltip && labelInfoIcon ? (
                  <Tooltip
                    behavior="hover"
                    trigger={
                      <span
                        className={s.chartHintBtn}
                        tabIndex={0}
                        role="img"
                        aria-label={t('Справка')}
                      >
                        !
                      </span>
                    }
                    optionsWrapperClassName={cn(s.chartTooltipPanel, tooltipPanelClassName)}
                  >
                    <div>{t(labelTooltip)}</div>
                  </Tooltip>
                ) : null}
              </div>
              <DashboardChartsWidget name={t(label)} invalidData={invalidData} filters={filters} />
            </div>
            {subLabel && <span className={s.subLabel}>{t(subLabel)}</span>}
            <div className={cn(s.chart, wrapped && s.wrapper)}>
              {other.series?.length ? <Chart height="400px" {...other} /> : <DashboardEmpty />}
            </div>
            {footer && <div className={s.footer}>{footer}</div>}
          </Card>
        ))}
    </div>
  );
};
